import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { enableAnalytics } from '@config/firebase';

type ConsentValue = 'accepted' | 'rejected';

const STORAGE_KEY = 'roomza_cookie_consent_v1';

interface CookieConsentContextType {
  /** null = not yet decided. */
  consent: ConsentValue | null;
  bannerOpen: boolean;
  acceptOptional: () => void;
  rejectOptional: () => void;
  openSettings: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

const readStoredConsent = (): ConsentValue | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
};

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentValue | null>(() => readStoredConsent());
  const [bannerOpen, setBannerOpen] = useState<boolean>(() => readStoredConsent() === null);

  // Honour a previously-granted consent on load (never before it).
  useEffect(() => {
    if (consent === 'accepted') {
      void enableAnalytics();
    }
  }, [consent]);

  const persist = useCallback((value: ConsentValue) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore storage errors (private mode, etc.) */
    }
    setConsent(value);
    setBannerOpen(false);
  }, []);

  const value = useMemo<CookieConsentContextType>(
    () => ({
      consent,
      bannerOpen,
      acceptOptional: () => persist('accepted'),
      rejectOptional: () => persist('rejected'),
      openSettings: () => setBannerOpen(true),
    }),
    [consent, bannerOpen, persist]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};
